import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table, Radio, Empty, DatePicker, Popover, Button, Modal, Checkbox, Input, message as antdMessage } from "antd";
import {
    FilterOutlined,
    ClearOutlined,
    PhoneOutlined,
    MessageOutlined,
    GiftOutlined,
    HeartOutlined,
    TagOutlined,
    CrownOutlined,
    SearchOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import moment from "moment";
import { Autocomplete, TextField } from "@mui/material";
import DashboardTableHeaderStyles from "../Pages/DashboardTableHeaderStyles";
import { CREATE_jwel1 } from "../../Config/Config";
import "../Assets/css/Style.css";
import PdfExcelPrint from "../Utiles/PdfExcelPrint"; // adjust path to match your folder

/* ─── Responsive hook (same pattern as MailBook.jsx) ──────────── */
const useWindowSize = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handler = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);
    return width;
};

const Category_Options = [
    "CUSTOMER",
    "DEALER",
    "WORKER",
    "INCHARGE",
    "PERSONAL",
    "OFFICIAL",
];

/* Only this customer type is shown on this screen */
const ALLOWED_CUST_TYPE = "CUSTOMER";

/* Master data uses "-" / null / "" for empty values — skip those in filter option lists */
const isRealValue = (v) => {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    return s !== "" && s !== "-";
};

/* ─── Design tokens — same navy/gold brand as MailBook.jsx ────── */
const T = {
    navyDark: "#0f2460",
    navyMid: "#1a3a8f",
    navyDeep: "#030354",
    gold: "#f5c518",
    ink: "#1a1a2e",
    sub: "#6b7280",
    faint: "#9ca3af",
    border: "#b7bfd6",
    chipBg: "#eef0fb",
    nameColor: "#0f2460",
    phoneBg: "#eaf4ff",
    phoneColor: "#0a5ea8",
    phoneBorder: "#c8e3fb",
    font: "Roboto, sans-serif",
};

/* ─── Reusable field styling — fixes faint/invisible borders ──── */
const fieldStyle = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "7px",
        fontFamily: T.font,
        fontSize: 13,
        backgroundColor: "#fff",
        "& fieldset": {
            borderColor: `${T.border} !important`,
            borderWidth: "1.5px !important",
        },
        "&:hover fieldset": {
            borderColor: `${T.navyMid} !important`,
        },
        "&.Mui-focused fieldset": {
            borderColor: `${T.navyMid} !important`,
            borderWidth: "1.5px !important",
        },
    },
    "& .MuiInputBase-input": {
        padding: "8.5px 10px",
    },
};

/* antd DatePicker sizing to line up with the MUI fields beside it */
const datePickerStyle = {
    width: "100%",
    height: 37,
    borderRadius: 7,
    fontFamily: T.font,
    fontSize: 13,
    border: `1.5px solid ${T.border}`,
};

/* ─── SMS campaign type metadata — icon, color, default template ──
   Edit SMS_TYPE_META[...].template to match your firm's copy, and
   wire handleSendSms's axios call to your real bulk-SMS endpoint. ── */
const SMS_TYPE_META = {
    birthday: {
        label: "Birthday",
        icon: <GiftOutlined />,
        color: "#e85d9c",
        bg: "#fdeef5",
        border: "#f7c6de",
        template:
            "Happy Birthday {Name}! 🎉 Wishing you a wonderful year ahead. — Team",
        candidateFilter: (d) => !!d.dob,
        dateField: "dob",
        dateLabel: "Date of Birth",
    },
    anniversary: {
        label: "Anniversary",
        icon: <HeartOutlined />,
        color: "#c0392b",
        bg: "#fdecea",
        border: "#f5b8b0",
        template:
            "Happy Anniversary {Name}! 💍 Wishing you many more years of happiness. — Team",
        candidateFilter: (d) => !!d.ANNVERSARY,
        dateField: "ANNVERSARY",
        dateLabel: "Anniversary",
    },
    offers: {
        label: "Offers",
        icon: <TagOutlined />,
        color: "#2e7d32",
        bg: "#eaf7ec",
        border: "#b7e0bb",
        template:
            "Hi {Name}, an exclusive offer is waiting for you! Visit us to know more. — Team",
        candidateFilter: () => true,
        dateField: null,
        dateLabel: null,
    },
    festivals: {
        label: "Festivals",
        icon: <CrownOutlined />,
        color: "#b8860b",
        bg: "#fdf6e3",
        border: "#f0d998",
        template:
            "Dear {Name}, wishing you and your family a joyous festive season! — Team",
        candidateFilter: () => true,
        dateField: null,
        dateLabel: null,
    },
};

/* ─── Combined stats row — one stat block inside the shared card ── */
const StatBlock = ({ label, value, caption, accent, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05, duration: 0.3, ease: "easeOut" }}
        style={{
            flex: "1 1 110px",
            minWidth: 100,
            padding: "2px 14px",
            borderLeft: index === 0 ? "none" : `1px solid ${T.border}`,
        }}
    >
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10.5,
                color: T.sub,
                fontFamily: T.font,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 4,
            }}
        >
            <span
                style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: accent,
                    flexShrink: 0,
                }}
            />
            {label}
        </div>
        <div
            style={{
                fontSize: 21,
                fontWeight: 700,
                color: T.ink,
                fontFamily: T.font,
                lineHeight: 1.1,
            }}
        >
            {value}
        </div>
        {caption && (
            <div
                style={{
                    fontSize: 10,
                    color: T.faint,
                    fontFamily: T.font,
                    marginTop: 2,
                }}
            >
                {caption}
            </div>
        )}
    </motion.div>
);

/* ─── Styled name cell (avatar initial + bold name) ─────────────── */
const NameCell = ({ name }) => {
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "#d1fae5",
                    color: "#065f46",
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: T.font,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {initial}
            </div>
            <span
                style={{
                    display: "inline-block",
                    background: "#d1fae5",
                    color: "#065f46",
                    fontWeight: 700,
                    fontSize: "11px",
                    padding: "2px 8px",
                    borderRadius: "4px",

                }}
            >
                {name || "—"}
            </span>
        </div>
    );
};

/* ─── Styled phone cell (pill, monospace) ───────────────────────── */
const PhoneCell = ({ number }) => {
    if (!number) return <span style={{ color: T.faint }}>—</span>;
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "Roboto Mono, monospace",
                color: T.phoneColor,
                background: T.phoneBg,
                border: `1px solid ${T.phoneBorder}`,
                borderRadius: 20,
                padding: "2px 10px 2px 8px",
                letterSpacing: "0.2px",
            }}
        >
            <PhoneOutlined style={{ fontSize: 11 }} />
            {number}
        </span>
    );
};

/* ─── Mobile read-only card ─────────────────────────────────────── */
const MobileRow = ({ item }) => (
    <div
        style={{
            backgroundColor: "#fff",
            borderRadius: 12,
            boxShadow: "0 1px 8px rgba(3,3,84,0.08)",
            padding: "14px 16px",
            marginBottom: 10,
            border: `1px solid ${T.border}`,
            position: "relative",
        }}
    >
        <div
            style={{
                position: "absolute",
                top: 12,
                right: 14,
                background: T.chipBg,
                color: T.navyDeep,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 20,
                padding: "2px 8px",
                fontFamily: T.font,
            }}
        >
            #{item.sno}
        </div>

        <div style={{ marginBottom: 6, paddingRight: 44 }}>
            <NameCell name={item.Dealername} />
        </div>
        <div
            style={{
                display: "inline-block",
                fontSize: 10,
                fontWeight: 700,
                color: T.navyDeep,
                background: T.chipBg,
                borderRadius: 6,
                padding: "2px 8px",
                fontFamily: T.font,
                marginBottom: 10,
                letterSpacing: "0.5px",
            }}
        >
            {item.CustType}
        </div>

        <div
            style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px 12px",
            }}
        >
            <div>
                <div style={mobileLabelStyle}>Mobile</div>
                <PhoneCell number={item.Mobilenum} />
            </div>
            {[
                { label: "City", value: item.CityName || "—" },
                { label: "State", value: item.STATE || "—" },
                {
                    label: "Location",
                    value: item.STATION === "LOCAL" ? "Local" : "Out Station",
                },
                { label: "Date of Birth", value: item.dob || "—" },
                { label: "Anniversary", value: item.ANNVERSARY || "—" },
            ].map(({ label, value }) => (
                <div key={label}>
                    <div style={mobileLabelStyle}>{label}</div>
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#374151",
                            fontFamily: T.font,
                        }}
                    >
                        {value}
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const mobileLabelStyle = {
    fontSize: 10,
    color: T.faint,
    fontFamily: T.font,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 2,
};

/* ─── Main Component ────────────────────────────────────────────── */
const MailBookRegister = () => {
    const tenantName = localStorage.getItem("tenantName");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

    const [visibleCount, setVisibleCount] = useState(10);
    const loaderRef = useRef(null);

    const windowWidth = useWindowSize();
    const isMobile = windowWidth <= 768;
    const isTablet = windowWidth > 768 && windowWidth <= 1199;

    const responsive = {
        containerWidth: isMobile ? "100%" : isTablet ? "98%" : "100%",
        fontSize: isMobile ? 11 : isTablet ? 12 : 13,
        scrollY: isMobile ? "35vh" : isTablet ? "45vh" : "48vh",
    };

    /* ── filters ── */
    const [filters, setFilters] = useState({
        category: null,
        mobile: null,
        name: null,
        city: null,
        state: null,
        locationType: "",
        dob: "",
        anniversary: "",
    });

    const updateFilter = (key, value) =>
        setFilters((prev) => ({ ...prev, [key]: value }));

    const clearFilters = () => {
        setFilters({
            category: null,
            mobile: null,
            name: null,
            city: null,
            state: null,
            locationType: "",
            dob: "",
            anniversary: "",
        });
    };

    const activeFilterCount = Object.values(filters).filter(
        (v) => v !== null && v !== "",
    ).length;

    /* ── SMS campaign modal ── */
    const [smsModalOpen, setSmsModalOpen] = useState(false);
    const [smsType, setSmsType] = useState(null); // 'birthday' | 'anniversary' | 'offers' | 'festivals'
    const [smsSelectedKeys, setSmsSelectedKeys] = useState([]);
    const [smsSearch, setSmsSearch] = useState("");
    const [smsText, setSmsText] = useState("");
    const [sendingSms, setSendingSms] = useState(false);

    const openSmsModal = (type) => {
        setSmsType(type);
        setSmsSelectedKeys([]);
        setSmsSearch("");
        setSmsText(SMS_TYPE_META[type].template);
        setSmsModalOpen(true);
    };

    const closeSmsModal = () => {
        if (sendingSms) return;
        setSmsModalOpen(false);
    };

    /* ── data fetch (read-only — no insert/update/delete here) ──
       Only CustType === "CUSTOMER" records are kept. */
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${CREATE_jwel1}/api/Master/MasterDealerMasterList`,
                { headers: { tenantName } },
            );
            const rows = Array.isArray(response.data) ? response.data : [];
            setData(
                rows
                    .filter(
                        (item) =>
                            String(item.CustType || "").trim().toUpperCase() ===
                            ALLOWED_CUST_TYPE,
                    )
                    .map((item, index) => ({
                        ...item,
                        sno: index + 1,
                        key: index + 1,
                    })),
            );
        } catch {
            // Silent — register view degrades to an empty list rather than a form-blocking error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    /* ── infinite scroll (mobile) ── */
    useEffect(() => {
        if (!isMobile) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) setVisibleCount((p) => p + 10);
            },
            { threshold: 0.1 },
        );
        if (loaderRef.current) observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [isMobile, loaderRef.current]);

    useEffect(() => {
        setVisibleCount(10);
    }, [filters]);

    /* ── autocomplete option lists derived from live data ── */
    const nameOptions = useMemo(
        () =>
            Array.from(
                new Set((data || []).map((d) => d.Dealername).filter(Boolean)),
            ).sort(),
        [data],
    );

    const mobileOptions = useMemo(
        () =>
            Array.from(
                new Set((data || []).map((d) => d.Mobilenum).filter(Boolean)),
            ).sort(),
        [data],
    );

    /* City / State lists come straight from the master list response (no CITY_MASTER / STATE_MASTER calls) */
    const cityOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    (data || [])
                        .map((d) => d.CityName)
                        .filter(isRealValue)
                        .map((v) => String(v).trim()),
                ),
            ).sort(),
        [data],
    );

    const stateOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    (data || [])
                        .map((d) => d.STATE)
                        .filter(isRealValue)
                        .map((v) => String(v).trim()),
                ),
            ).sort(),
        [data],
    );

    /* ── filtering ── */
    const filteredData = useMemo(() => {
        return (data || []).filter((item) => {
            if (filters.category && item.CustType !== filters.category)
                return false;
            if (
                filters.mobile &&
                !`${item.Mobilenum || ""} ${item.MobileNum2 || ""}`.includes(
                    filters.mobile,
                )
            )
                return false;
            if (
                filters.name &&
                !(item.Dealername || "")
                    .toLowerCase()
                    .includes(filters.name.toLowerCase())
            )
                return false;
            if (filters.city && item.CityName !== filters.city) return false;
            if (filters.state && item.STATE !== filters.state) return false;
            if (filters.locationType && item.STATION !== filters.locationType)
                return false;
            if (filters.dob) {
                const itemDob = item.dob
                    ? moment(item.dob).format("YYYY-MM-DD")
                    : null;
                if (itemDob !== filters.dob) return false;
            }
            if (filters.anniversary) {
                const itemAnv = item.ANNVERSARY
                    ? moment(item.ANNVERSARY).format("YYYY-MM-DD")
                    : null;
                if (itemAnv !== filters.anniversary) return false;
            }
            return true;
        });
    }, [data, filters]);

    const stats = useMemo(() => {
        const total = data.length;
        const local = data.filter((d) => d.STATION === "LOCAL").length;
        const outStation = total - local;
        return { total, local, outStation, showing: filteredData.length };
    }, [data, filteredData]);

    /* ── SMS candidates for the active campaign type ── */
    const smsCandidates = useMemo(() => {
        if (!smsType) return [];
        const meta = SMS_TYPE_META[smsType];
        let list = data.filter(meta.candidateFilter);
        if (smsSearch.trim()) {
            const q = smsSearch.trim().toLowerCase();
            list = list.filter(
                (d) =>
                    (d.Dealername || "").toLowerCase().includes(q) ||
                    (d.Mobilenum || "").includes(q) ||
                    (d.CityName || "").toLowerCase().includes(q),
            );
        }
        return list;
    }, [data, smsType, smsSearch]);

    const smsColumns = useMemo(() => {
        const meta = smsType ? SMS_TYPE_META[smsType] : null;
        const cols = [
            {
                title: "Name",
                dataIndex: "Dealername",
                key: "Dealername",
                render: (name) => <NameCell name={name} />,
            },
            {
                title: "Mobile",
                dataIndex: "Mobilenum",
                key: "Mobilenum",
                render: (n) => <PhoneCell number={n} />,
            },
            { title: "City", dataIndex: "CityName", key: "CityName" },
        ];
        if (meta?.dateField) {
            cols.push({
                title: meta.dateLabel,
                dataIndex: meta.dateField,
                key: meta.dateField,
                render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "—"),
            });
        }
        return cols;
    }, [smsType]);

    const handleSendSms = async () => {
        if (smsSelectedKeys.length === 0) {
            antdMessage.warning("Select at least one recipient.");
            return;
        }
        if (!smsText.trim()) {
            antdMessage.warning("Message cannot be empty.");
            return;
        }

        setSendingSms(true);
        try {
            const recipients = smsCandidates
                .filter((d) => smsSelectedKeys.includes(d.key))
                .map((d) => ({
                    mobile: d.Mobilenum,
                    message: smsText.replace("{Name}", d.Dealername || ""),
                }));

            // NOTE: placeholder endpoint — point this at your actual bulk-SMS API.
            await axios.post(
                `${CREATE_jwel1}/api/Sms/SendBulk`,
                { tenantName, campaignType: smsType, recipients },
                { headers: { tenantName } },
            );

            antdMessage.success(
                `Message sent to ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}.`,
            );
            setSmsModalOpen(false);
        } catch (error) {
            console.error("Send SMS error:", error);
            antdMessage.error("Failed to send messages. Please try again.");
        } finally {
            setSendingSms(false);
        }
    };

    /* ── export column map for PdfExcelPrint (flat, print/pdf/excel-safe values) ── */
    const exportColumns = useMemo(
        () => [
            { title: "S.No", dataIndex: "sno", align: "center" },
            { title: "Category", dataIndex: "CustType" },
            { title: "Name", dataIndex: "Dealername" },
            { title: "Mobile No.1", dataIndex: "Mobilenum" },
            { title: "Mobile No.2", dataIndex: "MobileNum2" },
            { title: "City", dataIndex: "CityName" },
            { title: "State", dataIndex: "STATE" },
            { title: "Location Type", dataIndex: "locationLabel" },
            { title: "Date of Birth", dataIndex: "dobLabel" },
            { title: "Anniversary", dataIndex: "anniversaryLabel" },
        ],
        [],
    );

    const exportData = useMemo(
        () =>
            filteredData.map((item) => ({
                sno: item.sno,
                CustType: item.CustType,
                Dealername: item.Dealername,
                Mobilenum: item.Mobilenum,
                MobileNum2: item.MobileNum2,
                CityName: item.CityName,
                STATE: item.STATE,
                locationLabel: item.STATION === "LOCAL" ? "Local" : "Out Station",
                dobLabel: item.dob ? moment(item.dob).format("DD-MM-YYYY") : "",
                anniversaryLabel: item.ANNVERSARY
                    ? moment(item.ANNVERSARY).format("DD-MM-YYYY")
                    : "",
            })),
        [filteredData],
    );

    /* ── table columns (read-only — no action column) ── */
    const columns = [
        { title: "S.No", dataIndex: "sno", key: "sno", width: 60, align: "center" },
        { title: "Category", dataIndex: "CustType", key: "CustType" },
        {
            title: "Name",
            dataIndex: "Dealername",
            key: "Dealername",
            render: (name) => <NameCell name={name} />,
        },
        {
            title: "Mobile No.1",
            dataIndex: "Mobilenum",
            key: "Mobilenum",
            render: (number) => <PhoneCell number={number} />,
        },
        { title: "City", dataIndex: "CityName", key: "CityName" },
        { title: "State", dataIndex: "STATE", key: "STATE" },
        {
            title: "Location Type",
            dataIndex: "STATION",
            key: "STATION",
            render: (text) => (
                <span
                    style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: T.font,
                        backgroundColor: text === "LOCAL" ? "#e8f5e9" : "#fff3e0",
                        color: text === "LOCAL" ? "#2e7d32" : "#ef6c00",
                        border: `1px solid ${text === "LOCAL" ? "#a5d6a7" : "#ffcc02"}`,
                    }}
                >
                    {text === "LOCAL" ? "✓ Local" : "🚚 Out Station"}
                </span>
            ),
        },
        {
            title: "Date of Birth",
            dataIndex: "dob",
            key: "dob",
            render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "—"),
        },
        {
            title: "Anniversary",
            dataIndex: "ANNVERSARY",
            key: "ANNVERSARY",
            render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "—"),
        },
    ];

    const mobileRows = filteredData.slice(0, visibleCount);
    const hasMore = visibleCount < filteredData.length;

    /* ── Filters popover content — same fields as before, now inside a popover ── */
    const filtersPopoverContent = (
        <div
            style={{
                width: isMobile ? "78vw" : 640,
                maxWidth: "88vw",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
                padding: "4px 2px",
            }}
        >
            <div>
                <div style={filterLabelStyle}>Category</div>
                <Autocomplete
                    size="small"
                    options={Category_Options}
                    value={filters.category}
                    onChange={(e, v) => updateFilter("category", v)}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="All categories" sx={fieldStyle} />
                    )}
                />
            </div>

            <div>
                <div style={filterLabelStyle}>Mobile Number</div>
                <Autocomplete
                    size="small"
                    freeSolo
                    options={mobileOptions}
                    value={filters.mobile}
                    onChange={(e, v) => updateFilter("mobile", v)}
                    onInputChange={(e, v, reason) => {
                        if (reason === "input") updateFilter("mobile", v);
                    }}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Search mobile" sx={fieldStyle} />
                    )}
                />
            </div>

            <div>
                <div style={filterLabelStyle}>Name</div>
                <Autocomplete
                    size="small"
                    freeSolo
                    options={nameOptions}
                    value={filters.name}
                    onChange={(e, v) => updateFilter("name", v)}
                    onInputChange={(e, v, reason) => {
                        if (reason === "input") updateFilter("name", v);
                    }}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Search name" sx={fieldStyle} />
                    )}
                />
            </div>

            <div>
                <div style={filterLabelStyle}>City</div>
                <Autocomplete
                    size="small"
                    options={cityOptions}
                    value={filters.city}
                    onChange={(e, v) => updateFilter("city", v)}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="All cities" sx={fieldStyle} />
                    )}
                />
            </div>

            <div>
                <div style={filterLabelStyle}>State</div>
                <Autocomplete
                    size="small"
                    options={stateOptions}
                    value={filters.state}
                    onChange={(e, v) => updateFilter("state", v)}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="All states" sx={fieldStyle} />
                    )}
                />
            </div>

            <div>
                <div style={filterLabelStyle}>Location Type</div>
                <Radio.Group
                    value={filters.locationType}
                    onChange={(e) => updateFilter("locationType", e.target.value)}
                    style={{ display: "flex", gap: 6 }}
                >
                    <Radio.Button value="">All</Radio.Button>
                    <Radio.Button value="LOCAL">Local</Radio.Button>
                    <Radio.Button value="Out Station">Out Station</Radio.Button>
                </Radio.Group>
            </div>

            <div>
                <div style={filterLabelStyle}>Date of Birth</div>
                <DatePicker
                    value={filters.dob ? moment(filters.dob) : null}
                    onChange={(date) =>
                        updateFilter("dob", date ? date.format("YYYY-MM-DD") : "")
                    }
                    format="DD-MM-YYYY"
                    placeholder="Select date"
                    allowClear
                    style={datePickerStyle}
                />
            </div>

            <div>
                <div style={filterLabelStyle}>Anniversary</div>
                <DatePicker
                    value={filters.anniversary ? moment(filters.anniversary) : null}
                    onChange={(date) =>
                        updateFilter("anniversary", date ? date.format("YYYY-MM-DD") : "")
                    }
                    format="DD-MM-YYYY"
                    placeholder="Select date"
                    allowClear
                    style={datePickerStyle}
                />
            </div>

            <div
                style={{
                    gridColumn: isMobile ? "1" : "1 / -1",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 4,
                    paddingTop: 10,
                    borderTop: `1px solid ${T.border}`,
                }}
            >
                <button
                    onClick={clearFilters}
                    disabled={activeFilterCount === 0}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        border: "none",
                        background: "transparent",
                        cursor: activeFilterCount === 0 ? "default" : "pointer",
                        fontFamily: T.font,
                        fontSize: 12.5,
                        color: activeFilterCount === 0 ? T.faint : T.sub,
                    }}
                >
                    <ClearOutlined /> Clear all
                </button>
                <button
                    onClick={() => setFilterPopoverOpen(false)}
                    style={{
                        fontFamily: T.font,
                        fontSize: 12.5,
                        fontWeight: 600,
                        borderRadius: 7,
                        color: "#fff",
                        border: "none",
                        background: T.navyMid,
                        padding: "6px 16px",
                        cursor: "pointer",
                    }}
                >
                    Done
                </button>
            </div>
        </div>
    );

    return (
        <div
            style={{
                backgroundColor: "#f0f2f5",
                position: "relative",
                padding: isMobile ? "12px 10px" : "16px 20px",
            }}
            className="mbr-screen"
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                    maxWidth: 1400,
                    margin: "0 auto",
                }}
            >
                {/* ── Header ── */}
                <div
                    className="mbr-no-print"
                    style={{
                        borderRadius: 10,
                        overflow: "hidden",
                        background: `linear-gradient(90deg, ${T.navyDark} 0%, ${T.navyMid} 100%)`,
                        boxShadow: "0 2px 12px rgba(3,3,84,0.12)",
                    }}
                >
                    <div
                        style={{
                            padding: isMobile ? "16px 16px" : "18px 24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 12,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <motion.div
                                animate={{ opacity: [1, 0.4, 1] }}
                                transition={{ duration: 1.8, repeat: Infinity }}
                                style={{
                                    width: 9,
                                    height: 9,
                                    borderRadius: "50%",
                                    background: T.gold,
                                }}
                            />
                            <div>
                                <div
                                    style={{
                                        fontWeight: 700,
                                        color: "#fff",
                                        fontFamily: T.font,
                                        fontSize: isMobile ? 15 : 17,
                                        letterSpacing: "0.3px",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Mail Book Register
                                </div>
                                <div
                                    style={{
                                        color: "rgba(255,255,255,0.65)",
                                        fontFamily: T.font,
                                        fontSize: 11.5,
                                        marginTop: 2,
                                    }}
                                >
                                    Read-only directory — filter, export, or print
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Popover
                                open={filterPopoverOpen}
                                onOpenChange={setFilterPopoverOpen}
                                trigger="click"
                                placement="bottomRight"
                                content={filtersPopoverContent}
                                overlayInnerStyle={{ borderRadius: 10 }}
                            >
                                <button
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        fontFamily: T.font,
                                        fontSize: 12.5,
                                        fontWeight: 600,
                                        borderRadius: 7,
                                        color: "#fff",
                                        border: "1px solid rgba(255,255,255,0.35)",
                                        background: "rgba(255,255,255,0.08)",
                                        padding: "7px 14px",
                                        cursor: "pointer",
                                    }}
                                >
                                    <FilterOutlined />
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <span
                                            style={{
                                                fontSize: 10.5,
                                                fontWeight: 700,
                                                color: T.navyDeep,
                                                background: T.gold,
                                                borderRadius: 20,
                                                padding: "1px 6px",
                                                marginLeft: 2,
                                            }}
                                        >
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>
                            </Popover>

                            <PdfExcelPrint
                                data={exportData}
                                columns={exportColumns}
                                fileName={`MailBookRegister_${moment().format("YYYYMMDD_HHmmss")}`}
                                disabled={filteredData.length === 0}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Stats card (left) + SMS card (right) ── */}
                <div
                    className="mbr-no-print"
                    style={{
                        display: "flex",
                        gap: 14,
                        flexWrap: "wrap",
                        alignItems: "stretch",
                    }}
                >
                    {/* Combined stats — single card */}
                    <div
                        style={{
                            flex: "2 1 460px",
                            minWidth: 300,
                            background: "#fff",
                            borderRadius: 10,
                            padding: "14px 6px",
                            boxShadow: "0 1px 8px rgba(3,3,84,0.06)",
                            border: `1px solid ${T.border}`,
                            display: "flex",
                            alignItems: "stretch",
                            flexWrap: "wrap",
                        }}
                    >
                        <StatBlock
                            label="Total Records"
                            value={stats.total}
                            caption="All entries"
                            accent={T.navyMid}
                            index={0}
                        />
                        <StatBlock
                            label="Local"
                            value={stats.local}
                            caption={`Of ${stats.total}`}
                            accent="#2e7d32"
                            index={1}
                        />
                        <StatBlock
                            label="Out Station"
                            value={stats.outStation}
                            caption={`Of ${stats.total}`}
                            accent="#ef6c00"
                            index={2}
                        />
                        <StatBlock
                            label="Showing"
                            value={stats.showing}
                            caption={activeFilterCount > 0 ? "Filtered" : "No filters"}
                            accent={T.gold}
                            index={3}
                        />
                    </div>

                    {/* SMS campaign card */}
                    <div
                        style={{
                            flex: "1 1 300px",
                            minWidth: 260,
                            background: "#fff",
                            borderRadius: 10,
                            padding: "14px 16px",
                            boxShadow: "0 1px 8px rgba(3,3,84,0.06)",
                            border: `1px solid ${T.border}`,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                color: T.navyDeep,
                                fontFamily: T.font,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                marginBottom: 10,
                            }}
                        >
                            <MessageOutlined /> SMS
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 8,
                            }}
                        >
                            {Object.entries(SMS_TYPE_META).map(([key, meta]) => (
                                <button
                                    key={key}
                                    onClick={() => openSmsModal(key)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 6,
                                        fontFamily: T.font,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        borderRadius: 8,
                                        color: meta.color,
                                        background: meta.bg,
                                        border: `1px solid ${meta.border}`,
                                        padding: "9px 6px",
                                        cursor: "pointer",
                                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.boxShadow = `0 4px 10px -4px ${meta.color}55`;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "none";
                                        e.currentTarget.style.boxShadow = "none";
                                    }}
                                >
                                    {meta.icon} {meta.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── List / Table ── */}
                <div
                    className="mbr-print-area"
                    style={{
                        backgroundColor: isMobile ? "transparent" : "white",
                        borderRadius: 10,
                        boxShadow: isMobile ? "none" : "0 2px 12px rgba(3,3,84,0.06)",
                        overflow: "hidden",
                        width: responsive.containerWidth,
                        alignSelf: "center",
                    }}
                >
                    {isMobile ? (
                        <div>
                            {mobileRows.length === 0 ? (
                                <div style={emptyStateStyle}>
                                    <Empty description="No records match these filters" />
                                </div>
                            ) : (
                                mobileRows.map((item) => (
                                    <MobileRow key={item.key} item={item} />
                                ))
                            )}
                            {hasMore && (
                                <div
                                    ref={loaderRef}
                                    className="mbr-no-print"
                                    style={{
                                        textAlign: "center",
                                        padding: "16px 0",
                                        color: T.faint,
                                        fontFamily: T.font,
                                        fontSize: 12,
                                    }}
                                >
                                    Loading more…
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ padding: isMobile ? "0 10px 16px" : "0 0 0" }}>
                            <DashboardTableHeaderStyles>
                                <Table
                                    columns={columns}
                                    dataSource={filteredData}
                                    rowKey="key"
                                    size="small"
                                    loading={loading}
                                    pagination={{ pageSize: 15, showSizeChanger: true }}
                                    scroll={{ x: "max-content", y: responsive.scrollY }}
                                    style={{ fontFamily: T.font, fontSize: responsive.fontSize, minWidth: isTablet ? 760 : "unset" }}
                                    rowClassName={(_, i) => (i % 2 === 0 ? "mbr-row-even" : "mbr-row-odd")}
                                    locale={{ emptyText: <Empty description="No records match these filters" /> }}
                                />
                            </DashboardTableHeaderStyles>
                        </div>
                    )}
                </div>
            </div>

            {/* ── SMS send modal — recipient multi-select + message + send ── */}
            <Modal
                title={
                    smsType && (
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: SMS_TYPE_META[smsType].color }}>
                                {SMS_TYPE_META[smsType].icon}
                            </span>
                            Send {SMS_TYPE_META[smsType].label} Message
                        </span>
                    )
                }
                open={smsModalOpen}
                onCancel={closeSmsModal}
                footer={null}
                width={760}
                maskClosable={!sendingSms}
                destroyOnClose
            >
                {smsType && (
                    <div style={{ fontFamily: T.font }}>
                        {/* Message composer */}
                        <div style={{ marginBottom: 14 }}>
                            <div style={filterLabelStyle}>Message</div>
                            <Input.TextArea
                                value={smsText}
                                onChange={(e) => setSmsText(e.target.value)}
                                rows={3}
                                placeholder="Type your message…"
                                style={{ fontFamily: T.font, fontSize: 13 }}
                            />
                            <div style={{ fontSize: 11, color: T.faint, marginTop: 4 }}>
                                Use <code>{"{Name}"}</code> — it's replaced with each recipient's name.
                            </div>
                        </div>

                        {/* Recipient search */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 8,
                                gap: 10,
                                flexWrap: "wrap",
                            }}
                        >
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                                Recipients
                                <span style={{ color: T.faint, fontWeight: 500 }}>
                                    {" "}
                                    ({smsCandidates.length} eligible
                                    {smsSelectedKeys.length > 0
                                        ? `, ${smsSelectedKeys.length} selected`
                                        : ""}
                                    )
                                </span>
                            </div>
                            <Input
                                allowClear
                                size="small"
                                placeholder="Search name, mobile, city"
                                prefix={<SearchOutlined style={{ color: T.faint }} />}
                                value={smsSearch}
                                onChange={(e) => setSmsSearch(e.target.value)}
                                style={{ width: 220, borderRadius: 7 }}
                            />
                        </div>

                        {/* Recipient table */}
                        <Table
                            size="small"
                            dataSource={smsCandidates}
                            columns={smsColumns}
                            rowKey="key"
                            pagination={{ pageSize: 6, size: "small" }}
                            scroll={{ y: 240 }}
                            rowSelection={{
                                selectedRowKeys: smsSelectedKeys,
                                onChange: (keys) => setSmsSelectedKeys(keys),
                            }}
                            locale={{
                                emptyText: (
                                    <Empty description="No matching records for this campaign" />
                                ),
                            }}
                            style={{ fontFamily: T.font, fontSize: 12.5 }}
                        />

                        {/* Actions */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 10,
                                marginTop: 16,
                            }}
                        >
                            <Button onClick={closeSmsModal} disabled={sendingSms}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={handleSendSms}
                                loading={sendingSms}
                                disabled={smsSelectedKeys.length === 0}
                                style={{ background: T.navyMid, borderColor: T.navyMid }}
                            >
                                Send to {smsSelectedKeys.length || ""} recipient
                                {smsSelectedKeys.length === 1 ? "" : "s"}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            <style>{`
        .mbr-row-even { background-color: #ffffff; }
        .mbr-row-odd  { background-color: #fafbff; }
        .ant-table-tbody > tr.mbr-row-even:hover > td,
        .ant-table-tbody > tr.mbr-row-odd:hover  > td {
          background-color: ${T.chipBg} !important;
        }
          .MuiOutlinedInput-root {
          border: 1px solid #e5e7eb !important;
          }
      `}</style>
        </div>
    );
};

const filterLabelStyle = {
    fontSize: 11.5,
    fontWeight: 600,
    color: T.sub,
    fontFamily: T.font,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
};

const emptyStateStyle = {
    textAlign: "center",
    padding: "40px 20px",
    background: "white",
    borderRadius: 10,
};

export default MailBookRegister;